import {QueryOptions} from '@apollo/client';

import {ArchiveRecord} from 'archive/ArchiveConstants';
import * as statusbar from 'components/StatusBar';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import ApolloFileState from 'filestate/ApolloFileState';
import {FileStateAttributes} from 'filestate/FileState';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {BufferState, BufferStates} from 'Navigation';

import AnalysisTemplateTabState from './AnalysisTemplateTabState';
import {
  GetAnalysisTemplate_Query as GetAnalysisTemplateQuery,
  GetAnalysisTemplateVariables,
} from './graphql/__generated/GetAnalysisTemplate';
import {GET_ANALYSIS_TEMPLATE_QUERY} from './graphql/GetAnalysisTemplate';

type AnalysisTemplate = GetAnalysisTemplateQuery['analysisTemplate'];

interface AnalysisTemplateFileStateAttributes {
  analysisTemplate?: AnalysisTemplate;
  editUrl?: string;
  name?: string;
  archiveRecord?: ArchiveRecord | null;
}

class AnalysisTemplateFileState extends ApolloFileState<
  GetAnalysisTemplateQuery,
  GetAnalysisTemplateVariables,
  AnalysisTemplateFileStateAttributes
> {
  static fileType = ModelTypeConstants.ANALYSIS_TEMPLATE;
  static override getQueryOptions(analysisTemplateId: string): QueryOptions<GetAnalysisTemplateVariables> {
    return {
      query: GET_ANALYSIS_TEMPLATE_QUERY,
      variables: {
        analysisTemplateId,
      },
    };
  }

  static override dataToAttributes(data: GetAnalysisTemplateQuery): AnalysisTemplateFileStateAttributes {
    const {analysisTemplate} = data;
    return isInaccessible(analysisTemplate)
      ? {}
      : {
          analysisTemplate,
          editUrl: analysisTemplate.editUrl,
          name: analysisTemplate.name,
          archiveRecord: analysisTemplate.archiveRecord,
        };
  }

  static override openFile(fileId: string): JQueryPromise<AnalysisTemplateFileState> {
    return super.openFile(fileId) as JQueryPromise<AnalysisTemplateFileState>;
  }

  override defaults(): FileStateAttributes {
    return {
      ...super.defaults(),
      toolbar: new Backbone.Model(),
      statusbar: new statusbar.StatusBarState(),
    };
  }

  override initialize(...args: Array<unknown>): void {
    super.initialize(...args);

    const buffers = new BufferStates([new BufferState()]);
    this.set({buffers});
    this.buffers()
      .at(0)
      .tabs()
      .add(
        new AnalysisTemplateTabState({
          analysisTemplateId: this.id,
        })
      );

    this.buffers().at(0).set({active: true});
    this.setIsWorkspaceSplit(false);
  }

  override editURL(): string | undefined {
    return this.get('editUrl');
  }

  override name(): string {
    return this.get('name') ?? 'Loading...';
  }

  override archiveRecord(): ArchiveRecord | null {
    return this.get('archiveRecord') ?? null;
  }

  override setArchiveRecord(archiveRecord: ArchiveRecord | null): void {
    this.set('archiveRecord', archiveRecord);
  }

  // This is present so that when an analysis template is archived due to its project being archived, an error isn't
  // thrown due to setPermissions being absent. However, we don't need to do anything here because the
  // AnalysisTemplate view will poll if opened and fetch the updated permissions.
  override setPermissions(): void {}

  override registerTitleAndURLSync(): void {
    this.on('change:name change:url', () => {
      this.syncTitleAndURL();
    });
  }
}

export default AnalysisTemplateFileState;
